// ** React Imports
import React, { Fragment, useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import styled from 'styled-components';
import classNames from 'classnames';

// ** Reactstrap Imports
import {
    Row,
    Col,
    Spinner,
    Container,
} from "reactstrap";
import { Button, Badge } from "react-bootstrap";
import { BiImport } from "react-icons/bi";
import { AiOutlineEye } from "react-icons/ai";
import { BsCheckCircleFill, BsCheckCircle } from "react-icons/bs";
// Utility-Service
import { getToken, getUser } from "../../../utility/AuthService";

// API Services
import { getAssesmentSummaryService } from "../../../services/submissionService";
import { getImageIndicatorService } from "../../../services/indicatorService";
import { getSubmittedDocumentServices } from "../../../services/uploadDocumentServices/getSubmittedDocumentServices";

import NavbarComponent from "../../components/layouts/Navbar";
import Footer from '../../components/layouts/Footer'
import { Bar } from "react-chartjs-2";
import ShowPdfModal from "../../components/modal/ShowPdfModal";

import toast, { Toaster } from 'react-hot-toast'
import CryptoJS from 'crypto-js';
import moment from 'moment'
import ESGMaturityTooltip from '../../components/common/ESGMaturityTooltip';
import { ESG_MATURITY_LEVELS } from '../../../constants/esgMaturityLevels';
import { selectLanguageUse } from '../../../redux/slices/language.Slice';

// Register Chart.js scales
Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
// Import PDF Components
import PdfReportPage2 from '../pdfReport/PdfReportPage2';
import ESGCertificate from '../pdfReport/ESGCertificate';
import { getCertificateBySubmissionIdService, checkCertificateApprovalStatusService } from '../../../services/certificateService';
// Import SDG Icons
import _5 from "../../../assets/images/analytics/indicatorImages/5.png"
import _6 from "../../../assets/images/analytics/indicatorImages/6.png"
import _7 from "../../../assets/images/analytics/indicatorImages/7.png"
import _8 from "../../../assets/images/analytics/indicatorImages/8.png"
import _12 from "../../../assets/images/analytics/indicatorImages/12.png"
import _13 from "../../../assets/images/analytics/indicatorImages/13.png"
import _16 from "../../../assets/images/analytics/indicatorImages/16.png"

const StyledDashboard = styled.div`
    .nav-tabs {
        border-bottom: 1px solid #dee2e6;
        margin-bottom: 2rem;
    }

    .tab-button {
        padding: 0.75rem 1.5rem;
        margin-right: 1rem;
        border: none;
        background: none;
        color: #6c757d;
        font-weight: 500;
        position: relative;
        cursor: pointer;

        &.active {
            color: #0d6efd;
            &:after {
                content: '';
                position: absolute;
                bottom: -1px;
                left: 0;
                right: 0;
                height: 2px;
                background-color: #0d6efd;
            }
        }
    }

    .progress-assessment-content {
        .overall-score {
            background: #f8f9fa;
            padding: 2rem;
            border-radius: 8px;
            margin-bottom: 2rem;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);

            h2 {
                color: #0d6efd;
                font-size: 3.5rem;
                margin: 0;
                font-weight: 600;
            }

            .score-label {
                color: #6c757d;
                font-size: 1.1rem;
                margin-bottom: 1rem;
            }
        }

        .dimension-scores {
            background: white;
            padding: 1.5rem;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);

            h4 {
                color: #495057;
                margin-bottom: 1.5rem;
                font-weight: 600;
            }

            .dimension-score-item {
                background: #f8f9fa;
                padding: 1.25rem;
                border-radius: 6px;
                margin-bottom: 1rem;
                transition: transform 0.2s;

                &:hover {
                    transform: translateY(-2px);
                }

                h5 {
                    margin-bottom: 0.75rem;
                    color: #495057;
                    font-weight: 500;
                }

                .progress {
                    height: 0.75rem;
                    background-color: #e9ecef;
                    border-radius: 0.5rem;
                    overflow: hidden;

                    .progress-bar {
                        background-color: #0d6efd;
                        transition: width 0.6s ease;
                    }
                }

                .score-details {
                    display: flex;
                    justify-content: space-between;
                    margin-top: 0.5rem;
                    font-size: 0.9rem;
                    color: #6c757d;
                }
            }
        }

        .question-answers {
            margin-top: 2rem;
            background: white;
            padding: 1.5rem;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);

            h4 {
                color: #495057;
                margin-bottom: 1.5rem;
                font-weight: 600;
            }

            .answer-item {
                padding: 1rem;
                border-bottom: 1px solid #e9ecef;
                
                &:last-child {
                    border-bottom: none;
                }

                .question {
                    font-weight: 500;
                    color: #495057;
                    margin-bottom: 0.5rem;
                }

                .answer {
                    color: #6c757d;
                    font-size: 0.95rem;
                }
            }
        }
    }

    .assessment-tabs {
        display: flex;
        gap: 1rem;
        margin-bottom: 2rem;
        border-bottom: 1px solid #dee2e6;
        padding-bottom: 0.5rem;

        .tab-button {
            padding: 0.75rem 1.5rem;
            border: none;
            background: none;
            color: #6c757d;
            font-weight: 500;
            position: relative;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
                color: #0d6efd;
            }

            &.active {
                color: #0d6efd;
                &:after {
                    content: '';
                    position: absolute;
                    bottom: -0.5rem;
                    left: 0;
                    right: 0;
                    height: 2px;
                    background-color: #0d6efd;
                }
            }
        }
    }

    .assessment-content {
        .chart-container {
            background: white;
            padding: 1.5rem;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            margin-bottom: 2rem;
        }

        .score-summary {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1rem;
            margin-bottom: 2rem;

            .score-card {
                background: white;
                padding: 1.5rem;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.05);

                h3 {
                    color: #495057;
                    font-size: 1.1rem;
                    margin-bottom: 1rem;
                }

                .score-value {
                    font-size: 2rem;
                    font-weight: 600;
                    color: #0d6efd;
                }
            }
        }
    }

    .activity-list {
        .activity-item {
            transition: background-color 0.2s ease;
            &:hover {
                background-color: #f8f9fa;
            }
            &:last-child {
                border-bottom: none !important;
            }
        }
    }

    .quick-actions {
        border: 1px solid #e9ecef;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        transition: transform 0.2s ease;
        &:hover {
            transform: translateY(-2px);
        }
    }

    .chart-container {
        background: white;
        padding: 1.5rem;
        border-radius: 0.5rem;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        margin-bottom: 1.5rem;
    }

    .recent-activity {
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        .activity-item {
            h5 {
                font-size: 1rem;
                font-weight: 500;
                color: #495057;
            }
            p {
                font-size: 0.875rem;
            }
        }
    }

    .assessment-selector {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 1.5rem;
        margin-bottom: 2rem;

        .assessment-card {
            background: white;
            border-radius: 12px;
            padding: 1.5rem;
            border: 2px solid transparent;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            cursor: pointer;
            transition: all 0.2s ease;
            position: relative;
            overflow: hidden;

            &:before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 4px;
                height: 100%;
                background: #0d6efd;
                opacity: 0;
                transition: opacity 0.2s ease;
            }

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            }

            &.active {
                border-color: #0d6efd;
                background: #f8f9ff;
                
                &:before {
                    opacity: 1;
                }

                h3 {
                    color: #0d6efd;
                }
            }

            h3 {
                font-size: 1.25rem;
                font-weight: 600;
                color: #2c3e50;
                margin-bottom: 0.75rem;
            }

            p {
                color: #6c757d;
                font-size: 0.95rem;
                margin-bottom: 1rem;
                line-height: 1.5;
            }

            .meta {
                display: flex;
                align-items: center;
                gap: 1rem;
                font-size: 0.875rem;
                color: #6c757d;

                .icon {
                    width: 20px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }
        }
    }
`;

const Dashboard = () => {
    const componentRef = useRef();
    const certificateRef = useRef();
    const baseUrl = process.env.REACT_APP_API_BASEURL
    const AWS_Url = process.env.REACT_APP_AWSURL
    const [isLoading, setLoading] = useState(false);
    const [dimensionData, setDimensionData] = useState([]);
    const [indicatorData, setIndicatorData] = useState([]);
    const [selectedIndicatorData, setSelectedIndicatorData] = useState([]);
    const [profileData, setProfileData] = useState();
    const [ESGperformanceData, setESGperformanceData] = useState();
    const [optionalQuesData, setOptionalQuesData] = useState([]);
    const [requiredQuestions, setrequiredQuestions] = useState([]);
    const [searchParams] = useSearchParams();
    const download = searchParams.get('download');
    const generateReport = searchParams.get('generateReport') === 'true';
    const [pdfContent, setPdfContent] = useState(false);
    const [certificatePdfContent, setCertificatePdfContent] = useState(false);
    const [certificateVerificationUrl, setCertificateVerificationUrl] = useState(null);
    const [certificateApproved, setCertificateApproved] = useState(false);
    const [certificateApprovalStatus, setCertificateApprovalStatus] = useState(null); // 'pending', 'approved', 'rejected', null
    const [overallVal, setOverallVal] = useState();
    const [overallValLebel, setOverallValLebel] = useState();
    const [overallColor, setOverallColor] = useState();
    const [imageList, setImageList] = useState([])
    const [dimensionList, setDimensionList] = useState([])
    const [uploadedDocInfo, setUploadedDocInfo] = useState([])
    const [pdfItem, setPdfItem] = useState();
    const [lastSubmission, setLastSubmission] = useState();
    const [showDocumentModal, setShowDocumentModal] = useState(false);

    const [showPdfModalShow, setShowPdfModalShow] = useState(false);
    const handleShowPdfModalShow = () => setShowPdfModalShow(!showPdfModalShow);

    const authToken = getToken()
    const { SId } = useParams()
    const user = getUser()
    const languageUse = useSelector(selectLanguageUse || user?.language)

    const decryptedSubId = CryptoJS.AES.decrypt(SId, 'mySecretKey').toString(CryptoJS.enc.Utf8);

    // Score processing functions
    const normalizeScore = (score, total) => {
        const numScore = Number(score);
        if (isNaN(numScore) || !total || numScore === 0) return '0.00';
        const percentage = (numScore / total) * 100;
        return Math.min(percentage, 100).toFixed(2);
    };

    // Helper function to check if industry score is available (not a placeholder)
    const isIndustryScoreAvailable = (indicator) => {
        return indicator && 
               !indicator.isIndustryScorePlaceholder && 
               indicator.industryScore !== null && 
               indicator.industryScore !== undefined &&
               indicator.originalIndustryScore !== indicator.total;
    };

    // Process SDG data with descriptions
    const processSDGData = (item) => {
        const descriptions = {
            'Water': {
                english: 'Ensure availability and sustainable management of water and sanitation for all',
                malay: 'Memastikan ketersediaan dan pengurusan air dan sanitasi yang mampan untuk semua'
            },
            'Human & Labour Rights': {
                english: 'Promote sustained, inclusive and sustainable economic growth, full and productive employment and decent work for all',
                malay: 'Menggalakkan pertumbuhan ekonomi yang berterusan, inklusif dan mampan, pekerjaan penuh dan produktif serta pekerjaan yang layak untuk semua'
            },
            'Living Wage': {
                english: 'End poverty in all its forms everywhere and ensure decent work and economic growth',
                malay: 'Mengakhiri kemiskinan dalam semua bentuk di mana-mana dan memastikan pekerjaan yang layak dan pertumbuhan ekonomi'
            },
            'Gender Equality': {
                english: 'Achieve gender equality and empower all women and girls',
                malay: 'Mencapai kesaksamaan gender dan memperkasakan semua wanita dan gadis'
            },
            'Anti-Corruption': {
                english: 'Promote peaceful and inclusive societies, provide access to justice for all and build effective, accountable and inclusive institutions',
                malay: 'Menggalakkan masyarakat yang aman dan inklusif, menyediakan akses kepada keadilan untuk semua dan membina institusi yang berkesan, bertanggungjawab dan inklusif'
            },
            'Climate Action': {
                english: 'Take urgent action to combat climate change and its impacts',
                malay: 'Mengambil tindakan segera untuk memerangi perubahan iklim dan kesannya'
            },
            'Energy Efficiency': {
                english: 'Ensure access to affordable, reliable, sustainable and modern energy for all',
                malay: 'Memastikan akses kepada tenaga yang mampu milik, boleh dipercayai, mampan dan moden untuk semua'
            },
            'Waste': {
                english: 'Ensure sustainable consumption and production patterns',
                malay: 'Memastikan corak penggunaan dan pengeluaran yang mampan'
            }
        };

        return {
            yourScore: normalizeScore(item.correct, item.total),
            industryAvg: isIndustryScoreAvailable(item) 
                ? (item.industryScore || normalizeScore(item.originalIndustryScore || item.industryScore, item.total))
                : null, // Return null if placeholder
            description: descriptions[item.name] || { english: '', malay: '' }
        };
    };

    const colorLeval = [
        {
            leval: "Laggard",
            color_code: "#FF0000"
        },
        {
            leval: "Beginner",
            color_code: "#FF9900"
        },
        {
            leval: "Intermediate",
            color_code: "#B6D7A8"
        },
        {
            leval: "Advanced",
            color_code: "#93C47D"
        },
        {
            leval: "Leader",
            color_code: "#38761D"
        },
    ]

    const [activeTab, setActiveTab] = useState('regular');
    const [progressDimensionData, setProgressDimensionData] = useState([]);
    const [progressScorePercent, setProgressScorePercent] = useState(0);
    const [progressQuestionAns, setProgressQuestionAns] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();

    const [showWelcomeModal, setShowWelcomeModal] = useState(true);
    const [assessmentOverview, setAssessmentOverview] = useState({
        totalSections: 0,
        completedSections: 0,
        estimatedTime: '30-45 minutes',
        lastSaved: null
    });

    const [submissionStatus, setSubmissionStatus] = useState({
        isSubmitting: false,
        progress: 0,
        currentStep: '',
        error: null
    });

    useEffect(() => {
        if (generateReport) {
            // Show document upload modal
            setShowDocumentModal(true);
        }
    }, [generateReport]);

    /**
    * @function getImage
    * @param (rowRecord, index)
    * @description update indicator image list
    */
    const getImage = async (rowRecord, index) => {
        if (rowRecord) {
            try {
                const result = await getImageIndicatorService({
                    token: authToken,
                    file: rowRecord
                });
                
                if (!result?.data) {
                    throw new Error('No data received from server');
                }

                const fileExtension = rowRecord?.filename?.split(".")[1] || 'png';
                const temp = [...imageList];
                temp[index] = `data:image/${fileExtension};base64,${Buffer.from(result.data, 'binary').toString('base64')}`;
                setImageList(temp);
            } catch (error) {
                // Silently handle image loading errors - fallback image will be used
                // Only log unexpected errors (not 404s)
                if (error.response?.status !== 404 && error.message !== 'Image not available') {
                    console.warn('Image loading warning:', error.message || 'Failed to load image');
                }
                // Set a placeholder or use default image - don't break the UI
                const temp = [...imageList];
                temp[index] = null; // Will use default/placeholder
                setImageList(temp);
            }
        } else {
            const temp = imageList;
            temp[index] = ''
            setImageList(temp)
        }
    }

    /**
     * @function getAssesmentSummary
     * @param ()
     * @description get Assesment Summary List
     */
    const getAssesmentSummary = async () => {
        try {
            setLoading(true);
            const result = await getAssesmentSummaryService({
                authToken,
                SubmissionId: decryptedSubId
            });

            // Process and normalize scores
            if (result?.data?.data?.indicaters) {
                console.log('Raw indicator data from API:', result.data.data.indicaters);
                
                result.data.data.indicaters = result.data.data.indicaters.map(indicator => {
                    // Check if industryScore is a placeholder (equals total)
                    const isPlaceholder = indicator.industryScore === indicator.total || 
                                         indicator.industryScore === null || 
                                         indicator.industryScore === undefined ||
                                         indicator.industryScore === 0;
                    
                    console.log(`Indicator ${indicator.name}:`, {
                        correct: indicator.correct,
                        total: indicator.total,
                        industryScore: indicator.industryScore,
                        isPlaceholder: isPlaceholder,
                        calculatedScore: normalizeScore(indicator.correct, indicator.total),
                        calculatedIndustryScore: isPlaceholder ? null : normalizeScore(indicator.industryScore, indicator.total)
                    });
                    
                    return {
                    ...indicator,
                        // Store original industryScore before normalization
                        originalIndustryScore: indicator.industryScore,
                        // Mark if it's a placeholder
                        isIndustryScorePlaceholder: isPlaceholder,
                        // Only normalize if not a placeholder, otherwise set to null
                        industryScore: isPlaceholder ? null : normalizeScore(indicator.industryScore, indicator.total),
                    score: normalizeScore(indicator.correct, indicator.total)
                    };
                });
            }

            setDimensionData(result?.data?.data?.dimensions);
            setIndicatorData(result?.data?.data?.indicaters);
            setProfileData(result?.data?.data?.profile);
            setESGperformanceData(result?.data?.data?.esgPerformance);
            setOptionalQuesData(result?.data?.data?.optionalQuestions);
            setrequiredQuestions(result?.data?.data?.requiredQuestions);
            setLastSubmission(result?.data?.data?.lastSubmissionDate);

            // Check certificate approval status
            try {
                const approvalResult = await checkCertificateApprovalStatusService({
                    authToken,
                    SubmissionId: decryptedSubId
                });
                if (approvalResult?.data?.success && approvalResult?.data?.data) {
                    const { isApproved, status, verificationUrl } = approvalResult.data.data;
                    setCertificateApproved(isApproved === true);
                    setCertificateApprovalStatus(status || null);
                    if (isApproved && verificationUrl) {
                        setCertificateVerificationUrl(verificationUrl);
                    }
                }
            } catch (error) {
                // Certificate not approved or doesn't exist yet
                console.log('Certificate not approved or not found for this submission');
                setCertificateApproved(false);
                setCertificateApprovalStatus(null);
            }

            // Process selected indicators with normalized scores
            const select = [];
            result?.data?.data?.dimensions.forEach((item) => {
                const dimensionIndicators = result?.data?.data?.indicaters.filter(
                    indicatorItem => indicatorItem.dimension.name === item.dimension
                );
                
                const normalizedIndicators = dimensionIndicators.map(indicator => ({
                    ...indicator,
                    normalizedScore: normalizeScore(indicator.correct, indicator.total)
                }));

                // Find the indicator with highest/lowest normalized score based on level
                const hasAdvancedOrHigher = normalizedIndicators.every(
                    ind => colorLeval.findIndex(element => element.leval === ind.leval) >= 2
                );

                const selectedIndicator = hasAdvancedOrHigher
                    ? normalizedIndicators.reduce((prev, current) => 
                        (parseFloat(prev.normalizedScore) > parseFloat(current.normalizedScore)) ? prev : current)
                    : normalizedIndicators.reduce((prev, current) => 
                        (parseFloat(prev.normalizedScore) < parseFloat(current.normalizedScore)) ? prev : current);

                select.push(selectedIndicator);
            });

            setSelectedIndicatorData(select);

            // Handle image fetching
            if (result?.data?.data?.indicaters) {
                const imagePromises = result.data.data.indicaters.map((indicator, index) => {
                    return indicator.file ? getImage(indicator.file, index) : getImage(null, index);
                });

                const results = await Promise.allSettled(imagePromises);
                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        console.error(`Failed to fetch image for indicator ${index}:`, result.reason);
                        setImageList(prev => ({ ...prev, [index]: '' }));
                    }
                });
            }
        } catch (error) {
            console.error('Assessment summary error:', error);
            toast.error(error?.response?.data?.message || 'Failed to load assessment data');
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentUpload = async (files) => {
        try {
            setLoading(true);
            // First create document entry
            const docResult = await uploadDocumentServices({
                authToken,
                submissionId: decryptedSubId,
                dimension: 'general',
                name: 'Assessment Documents',
                description: 'Uploaded assessment documents'
            });

            if (docResult?.data?.statusCode === 200) {
                // Then upload the files
                const uploadResult = await uploadPDFServices({
                    authToken,
                    id: docResult.data.data._id,
                    files
                });

                if (uploadResult?.data?.statusCode === 200) {
                    toast.success('Documents uploaded successfully');
                    setShowDocumentModal(false);
                    // Refresh assessment data
                    getAssesmentSummary();
                } else {
                    throw new Error(uploadResult?.data?.message || 'Failed to upload files');
                }
            } else {
                throw new Error(docResult?.data?.message || 'Failed to create document entry');
            }
        } catch (error) {
            console.error('Document upload error:', error);
            toast.error(error.message || 'Failed to upload documents. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
    * @function getSubmittedDocs
    * @param ()
    * @description get Submitted Documents
    */
    const getSubmittedDocs = async () => {
        if (!decryptedSubId) {
            console.log('No submission ID available');
            setUploadedDocInfo([]);
            return;
        }

        try {
            setLoading(true);
            
            // First, check localStorage for documents uploaded during assessment completion
            const localStorageDocs = localStorage.getItem(`uploaded_documents_${decryptedSubId}`);
            if (localStorageDocs) {
                try {
                const parsedDocs = JSON.parse(localStorageDocs);
                console.log('Found documents in localStorage:', parsedDocs);
                
                // Convert localStorage documents to the format expected by the dashboard
                const formattedDocs = [{
                    dimension: { name: 'Assessment Documents', name_Malay: 'Documents d\'évaluation' },
                    name: 'Supporting Documents',
                    description: 'Documents uploaded during assessment completion',
                        files: parsedDocs.map(doc => doc.name || doc) // Use file names as file references
                }];
                
                setUploadedDocInfo(formattedDocs);
                setLoading(false);
                return;
                } catch (parseError) {
                    console.error('Error parsing localStorage docs:', parseError);
                    // Continue to API fetch
                }
            }
            
            // Fetch from API
            try {
                console.log('Fetching documents from API for submission:', decryptedSubId);
                    const result = await getSubmittedDocumentServices({
                        authToken: authToken,
                        SubmissionId: decryptedSubId
                    });

                console.log('API response:', result?.data);

                if (result?.data?.statusCode === 200 && result?.data?.data) {
                    const docs = Array.isArray(result.data.data) ? result.data.data : [];
                    console.log('Setting uploaded documents:', docs);
                    setUploadedDocInfo(docs);
                    } else {
                    console.log('No documents found in API response or status code not 200:', result?.data);
                    setUploadedDocInfo([]);
                }
            } catch (apiError) {
                console.error('API fetch failed:', apiError);
                console.error('Error details:', {
                    message: apiError?.message,
                    response: apiError?.response?.data,
                    status: apiError?.response?.status
                });
                setUploadedDocInfo([]);
            }
            
            setLoading(false);
        } catch (error) {
            console.error('Error in getSubmittedDocs:', error);
            setLoading(false);
            setUploadedDocInfo([]);
        }
    };

    useEffect(() => {
        if (decryptedSubId) {
            getSubmittedDocs();
        }
    }, [decryptedSubId]);

    /**
    * @function downloadDoc
    * @param (data)
    * @description to download Document
    */
    const downloadDoc = (pdfFile) => {
        // using Java Script method to get PDF file
        fetch(pdfFile).then(response => {
            response.blob().then(blob => {
                // Creating new object of PDF file
                const fileURL = window.URL.createObjectURL(blob);
                // Setting various property values
                const alink = document.createElement('a');
                alink.href = fileURL;
                alink.download = pdfFile;
                alink.click();
            })
        })

    };

    /**
    * @function viewDoc
    * @param (data)
    * @description to view Document
    */
    const viewDoc = (item) => {
        setPdfItem(item)
        handleShowPdfModalShow()

    };

    const selectESGcolor = (value) => {
        if (value > 0 && value <= 20) {
            return '#FF0000'
        } else if (value > 20 && value <= 40) {
            return '#FF9900'
        } else if (value > 40 && value <= 60) {
            return '#B6D7A8'
        } else if (value > 60 && value <= 80) {
            return '#93C47D'
        } else if (value > 80 && value <= 100) {
            return '#38761D'
        }
    }

    useEffect(() => {
        if (dimensionData?.length > 0) {
            const labels = dimensionData?.length > 0 ? dimensionData?.map(item => languageUse === 'English' ? item.dimension : item.dimension_Malay) : [];
            labels.push('ESG');
            // labels.push('Overall');
            setOverallValLebel(labels)

            const totalValues = dimensionData?.length > 0 ? dimensionData?.map(item => (item.total)) : []
            let totalValuesSum = 0;
            totalValues.map(x => (totalValuesSum += x));

            const correctValues = dimensionData?.length > 0 ? dimensionData?.map(item => (item.correct)) : []
            let correctValuesSum = 0;
            correctValues.map(x => (correctValuesSum += x));

            const overallValue = ((correctValuesSum / totalValuesSum) * 100).toFixed(2)
            const ESGcolor = selectESGcolor(overallValue)

            const allColor = dimensionData?.length > 0 && dimensionData?.map(item => colorLeval.find(element => element.leval === item.leval).color_code)
            allColor.push(ESGcolor)
            setOverallColor(allColor)

            const chartData = dimensionData?.length > 0 && dimensionData?.map(item => ((item.correct / item.total) * 100).toFixed(2));
            chartData.push(overallValue);
            setOverallVal(chartData)
        }
    }, [dimensionData, languageUse]);

    const data = {
        labels: overallValLebel,
        datasets: [
            {
                label: "Score of Category",

                data: overallVal,
                backgroundColor: overallColor,
                // backgroundColor: dimensionData?.length > 0 ? dimensionData?.map(item => colorLeval.find(element => element.leval === item.leval).color_code) : [],

                borderColor: overallColor,
                borderWidth: 1,
            },
        ],
    };

    const options = {
        responsive: true,
        // maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: "Score of Category",
            },
            datalabels: {
                anchor: 'end',
                align: 'top',
                color: "black",
                display: function (context) {
                    return context.dataset.data[context.dataIndex] > 0;
                },
                font: {
                    weight: "bold",
                },
                formatter: (value) => {
                    return `${value}%`
                },
            },
        },
        scales: {
            x: {
                max: 100,
                ticks: {
                    callback: (value) => {
                        return `${value}%`
                    }
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                position: 'bottom'
            },
            y: {
                max: 150,
                ticks: {
                    display: false,
                },
                grid: {
                    display: false,
                },
                border: {
                    display: true,
                },
            },
        },
        maintainAspectRatio: true,

    };

    const horizontalData = {
        labels: indicatorData?.length > 0 && indicatorData?.map((item) => languageUse === 'English' ? item.name : item.name_Malay),
        datasets: [
            {
                label: languageUse === 'English' ? "Your Score" : "Markah anda",
                data: indicatorData?.length > 0 && indicatorData?.map((item) => {
                    const score = normalizeScore(item.correct, item.total);
                    return parseFloat(score);
                }),
                backgroundColor: indicatorData?.map((item) => item?.dimension?.color),
                borderColor: indicatorData?.map((item) => item?.dimension?.color),
                borderWidth: 1,
            },
            {
                label: languageUse === 'English' ? "Peer Score" : "Skor Rakan Sebaya",
                data: indicatorData?.length > 0 && indicatorData?.map((item) => {
                    // Only show peer score if it's available (not a placeholder)
                    if (!isIndustryScoreAvailable(item)) {
                        return null; // Chart.js will skip null values
                    }
                    // Use already normalized score if available, otherwise calculate
                    const score = item.industryScore !== null && item.industryScore !== undefined 
                        ? item.industryScore 
                        : normalizeScore(item.originalIndustryScore || item.industryScore, item.total);
                    return parseFloat(score);
                }),
                backgroundColor: indicatorData?.map((item) => item?.dimension?.peerColor),
                borderColor: indicatorData?.map((item) => item?.dimension?.peerColor),
                borderWidth: 1,
            },
        ],
    };


    const horoizontalOptions = {
        responsive: true,
        indexAxis: 'y',
        plugins: {
            legend: {
                display: false,
                labels: {
                    usePointStyle: true,
                    boxWidth: 20,
                    padding: 50,

                },

                position: 'bottom',
                align: 'center'
            },

            title: {
                display: true,
                text: "Score of Category",
            },
            datalabels: {

                display: false,

            },
        },
        scales: {
            x: {
                max: 100,
                ticks: {
                    callback: (value) => {
                        return `${value}%`
                    }
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                position: 'bottom'
            },
            y: {
                max: 150,
                ticks: {
                    // display: false,
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
            },
        },
        maintainAspectRatio: true,

    };

    /**
     * @function afterPrint
     * @param ()
     * @description set pdf content false after the print
    */
    const afterPrint = () => {
        setPdfContent(false)
    }

    /**
     * @function pdfContenFunc
     * @param ()
     * @description handle pdf content
    */
    // this footer is deafult
    const pdfContenFunc = useReactToPrint({
        pageStyle: `@media print {
                  @page {
                    margin: 20px !important;
                    background: green !important;
                  }
                  @page :footer {
                    display: none !important;
                  }

                  @page :header {
                    display: none !important;
                  }

                  html, body {
                    height: 99%;
                    width: 100% !important;
                    -webkit-print-color-adjust: exact;   
                  }
                }`,

        //     pageStyle: `@page {
        //     size: A4;
        //     margin: 0;
        // }
        // @media print {
        //     html, body {
        //         width: 210mm;
        //         height: 297mm;        
        //     }
        //     .page {
        //         margin: 0;
        //         border: initial;
        //         border-radius: initial;
        //         width: initial;
        //         min-height: initial;
        //         box-shadow: initial;
        //         background: initial;
        //         page-break-after: always;
        //     }
        // }`,
        content: () => componentRef.current,
        documentTitle: `${profileData?.company}_UNCG`,
        onAfterPrint: afterPrint
    });

    /**
     * @function pdfDownloadFunc
     * @param ()
     * @description handle pdf download with error handling and loading states
    */
    const pdfDownloadFunc = () => {
        // Check if component ref exists
        if (!componentRef.current) {
            toast.error(languageUse === 'English' 
                ? 'Report content is not ready. Please wait a moment and try again.' 
                : 'Le contenu du rapport n\'est pas prêt. Veuillez attendre un moment et réessayer.');
            return;
        }

        // Set loading state
        setPdfContent(true);
        setLoading(true);
        
        // Show loading message
        toast.loading(languageUse === 'English' 
            ? 'Preparing report for download...' 
            : 'Préparation du rapport pour le téléchargement...',
            { id: 'pdf-download' }
        );

        // Wait for component to render, then trigger print
        const timer = setTimeout(() => {
            try {
                if (componentRef.current) {
                    pdfContenFunc();
                    toast.success(languageUse === 'English' 
                        ? 'Report download started' 
                        : 'Téléchargement du rapport démarré',
                        { id: 'pdf-download' }
                    );
                } else {
                    throw new Error('Component ref not available');
                }
            } catch (error) {
                console.error('PDF download error:', error);
                toast.error(languageUse === 'English' 
                    ? 'Failed to download report. Please try again.' 
                    : 'Échec du téléchargement du rapport. Veuillez réessayer.',
                    { id: 'pdf-download' }
                );
            } finally {
                setLoading(false);
            }
        }, 1500); // Increased timeout to ensure component is ready

        return () => clearTimeout(timer);
    };

    // Certificate print handler
    const certificatePrintFunc = useReactToPrint({
        content: () => certificateRef.current,
        documentTitle: `${profileData?.company}_ESG_Certificate`,
        onAfterPrint: () => {
            setCertificatePdfContent(false);
            setLoading(false);
        }
    });

    /**
     * @function certificateDownloadFunc
     * @param ()
     * @description Download approved certificate (certificate must be approved by admin first)
    */
    const certificateDownloadFunc = () => {
        // Check if certificate is approved
        if (!certificateApproved) {
            toast.error(languageUse === 'English' 
                ? 'Certificate is not approved yet. Please wait for admin approval.' 
                : 'Sijil belum diluluskan. Sila tunggu kelulusan admin.');
            return;
        }

        // Check if component ref exists
        if (!certificateRef.current) {
            toast.error(languageUse === 'English' 
                ? 'Certificate content is not ready. Please wait a moment and try again.' 
                : 'Kandungan sijil tidak siap. Sila tunggu seketika dan cuba lagi.');
            return;
        }

        // Set loading state
        setCertificatePdfContent(true);
        setLoading(true);
        
        // Show loading message
        toast.loading(languageUse === 'English' 
            ? 'Preparing certificate for download...' 
            : 'Menyediakan sijil untuk dimuat turun...',
            { id: 'certificate-download' }
        );

        // Wait for component to render, then trigger print
        setTimeout(() => {
            try {
                if (certificateRef.current) {
                    certificatePrintFunc();
                    toast.success(languageUse === 'English' 
                        ? 'Certificate download started' 
                        : 'Muat turun sijil dimulakan',
                        { id: 'certificate-download' }
                    );
                } else {
                    throw new Error('Certificate ref not available');
                }
            } catch (error) {
                console.error('Certificate download error:', error);
                toast.error(languageUse === 'English' 
                    ? 'Failed to download certificate. Please try again.' 
                    : 'Gagal memuat turun sijil. Sila cuba lagi.',
                    { id: 'certificate-download' }
                );
                setLoading(false);
            }
        }, 1500);
    };

    useEffect(() => {
        if (download) {
            pdfDownloadFunc()
        }
    }, [selectedIndicatorData])

    // Update the comparison detector to use normalized scores
    const higherLowerDetector = (item) => {
        if (!item) return '';
        
        const yourScore = normalizeScore(item.correct, item.total);
        
        // Check if industry score is available
        if (!isIndustryScoreAvailable(item)) {
            return languageUse === 'English' ? 'Industry data not available' : 'Data industri tidak tersedia';
        }
        
        const peerScore = item.industryScore || normalizeScore(item.originalIndustryScore || item.industryScore, item.total);
        const difference = Math.abs(parseFloat(yourScore) - parseFloat(peerScore)).toFixed(2);
        
        if (parseFloat(yourScore) >= parseFloat(peerScore)) {
            return `${difference}% ${languageUse === 'English' ? 'higher' : 'yang lebih'}`;
        } else {
            return `${difference}% ${languageUse === 'English' ? 'lower' : 'yang kurang'}`;
        }
    };

    // Add this function to fetch progress assessment data
    const getProgressAssessmentSummary = async () => {
        try {
            setLoading(true);
            const result = await getProgressAssessmentSummaryService({
                authToken,
                SubmissionId: decryptedSubId
            });

            if (result?.data?.data) {
                const { data } = result.data;
                setProgressDimensionData(data?.dimensionScoring || []);
                setProgressScorePercent(Number(data?.scorePercent) || 0);
                setProgressQuestionAns(Array.isArray(data?.questionAnswers) ? data?.questionAnswers : []);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setProgressDimensionData([]);
            setProgressScorePercent(0);
            setProgressQuestionAns([]);
            toast.error(error?.response?.data?.message || 'Something went wrong');
        }
    };

    useEffect(() => {
        if (activeTab === 'progress') {
            getProgressAssessmentSummary();
        } else {
            getAssesmentSummary();
        }
    }, [activeTab]);

    // Separate chart data for regular and progress assessments
    const regularChartData = {
        labels: overallValLebel,
        datasets: [{
            label: "Regular Assessment Score",
            data: overallVal,
            backgroundColor: overallColor,
            borderColor: overallColor,
            borderWidth: 1,
        }]
    };

    const progressChartData = {
        labels: progressDimensionData.map(d => languageUse === 'English' ? d.name : d.name_French),
        datasets: [{
            label: "Progress Assessment Score",
            data: progressDimensionData.map(d => ((d.correct / d.total) * 100).toFixed(1)),
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            borderWidth: 1,
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: true,
                text: languageUse === 'English' ? "Assessment Scores" : "Skor Penilaian",
            },
            datalabels: {
                anchor: 'end',
                align: 'top',
                color: "black",
                display: true,
                font: { weight: "bold" },
                formatter: (value) => `${value}%`,
            },
        },
        scales: {
            x: {
                max: 100,
                ticks: {
                    callback: (value) => {
                        return `${value}%`
                    }
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                position: 'bottom',
                title: {
                    display: true,
                    text: 'Score (%)',
                    font: {
                        size: 14
                    },
                    padding: {
                        top: 10
                    }
                }
            },
            y: {
                max: 150,
                ticks: {
                    // display: false,
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                title: {
                    display: true,
                    text: 'Categories',
                    font: {
                        size: 14
                    },
                    padding: {
                        right: 10
                    }
                }
            },
        },
        maintainAspectRatio: true,
    };

    // Add welcome modal component
    const WelcomeModal = () => (
        <div className={`modal fade ${showWelcomeModal ? 'show' : ''}`} style={{ display: showWelcomeModal ? 'block' : 'none' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {languageUse === 'English' ? 'Welcome to Your ESG Assessment' : 'Selamat Datang ke Penilaian ESG Anda'}
                        </h5>
                        <button type="button" className="btn-close" onClick={() => setShowWelcomeModal(false)}></button>
                    </div>
                    <div className="modal-body">
                        <div className="assessment-overview p-4 bg-light rounded mb-4">
                            <h6 className="mb-3">
                                {languageUse === 'English' ? 'Your ESG Report Benefits' : 'Avantages de votre rapport ESG'}
                            </h6>
                            <div className="row">
                                <div className="col-md-6">
                                    <p>
                                        <strong>{languageUse === 'English' ? 'Comprehensive Analysis:' : 'Analyse complète:'}</strong> {languageUse === 'English' ? 'Get detailed insights into your ESG performance across all dimensions' : 'Obtenez des informations détaillées sur votre performance ESG dans toutes les dimensions'}
                                    </p>
                                    <p>
                                        <strong>{languageUse === 'English' ? 'Actionable Insights:' : 'Perspectives exploitables:'}</strong> {languageUse === 'English' ? 'Receive specific recommendations for improvement' : 'Recevez des recommandations spécifiques pour l\'amélioration'}
                                    </p>
                                </div>
                                <div className="col-md-6">
                                    <p>
                                        <strong>{languageUse === 'English' ? 'Progress Tracking:' : 'Suivi des progrès:'}</strong> {languageUse === 'English' ? 'Monitor your ESG journey over time' : 'Surveillez votre parcours ESG au fil du temps'}
                                    </p>
                                    <p>
                                        <strong>{languageUse === 'English' ? 'Industry Benchmarking:' : 'Évaluation comparative:'}</strong> {languageUse === 'English' ? 'Compare your performance with industry standards' : 'Comparez votre performance aux normes de l\'industrie'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="assessment-tips">
                            <h6 className="mb-3">
                                {languageUse === 'English' ? 'What You\'ll Get' : 'Ce que vous obtiendrez'}
                            </h6>
                            <ul className="list-unstyled">
                                <li className="mb-2">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    {languageUse === 'English' ? 'Detailed ESG score breakdown' : 'Répartition détaillée du score ESG'}
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    {languageUse === 'English' ? 'SDG alignment analysis' : 'Analyse de l\'alignement avec les ODD'}
                                </li>
                                <li className="mb-2">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    {languageUse === 'English' ? 'Customized improvement roadmap' : 'Feuille de route d\'amélioration personnalisée'}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-primary" onClick={() => setShowWelcomeModal(false)}>
                            {languageUse === 'English' ? 'Understand your ESG' : 'Comprendre votre ESG'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    // Add assessment progress tracking
    useEffect(() => {
        if (dimensionData?.length > 0) {
            setAssessmentOverview(prev => ({
                ...prev,
                totalSections: dimensionData.length,
                completedSections: dimensionData.filter(dim => dim.completed).length,
                lastSaved: new Date().toLocaleString()
            }));
        }
    }, [dimensionData]);

    // Add submission process tracking
    const handleSubmission = async () => {
        try {
            setSubmissionStatus({
                isSubmitting: true,
                progress: 0,
                currentStep: languageUse === 'English' ? 'Preparing submission...' : 'Préparation de la soumission...',
                error: null
            });

            // Step 1: Validate all required sections
            const validationResult = await validateSubmission();
            if (!validationResult.isValid) {
                throw new Error(validationResult.message);
            }

            setSubmissionStatus(prev => ({
                ...prev,
                progress: 25,
                currentStep: languageUse === 'English' ? 'Processing answers...' : 'Traitement des réponses...'
            }));

            // Step 2: Process answers
            await processAnswers();

            setSubmissionStatus(prev => ({
                ...prev,
                progress: 50,
                currentStep: languageUse === 'English' ? 'Generating report...' : 'Génération du rapport...'
            }));

            // Step 3: Generate report
            await generateReport();

            setSubmissionStatus(prev => ({
                ...prev,
                progress: 75,
                currentStep: languageUse === 'English' ? 'Finalizing...' : 'Finalisation...'
            }));

            // Step 4: Complete submission
            await completeSubmission();

            setSubmissionStatus(prev => ({
                ...prev,
                progress: 100,
                currentStep: languageUse === 'English' ? 'Submission complete!' : 'Soumission terminée!'
            }));

            // Show success message and redirect
            toast.success(languageUse === 'English' ? 'Assessment submitted successfully!' : 'Penilaian berjaya dihantar!');
            setTimeout(() => {
                navigate('/dashboard');
            }, 2000);

        } catch (error) {
            setSubmissionStatus(prev => ({
                ...prev,
                error: error.message,
                isSubmitting: false
            }));
            toast.error(error.message || (languageUse === 'English' ? 'Failed to submit assessment' : 'Échec de la soumission de l\'évaluation'));
        }
    };

    // Add submission progress modal
    const SubmissionProgressModal = () => (
        <div className={`modal fade ${submissionStatus.isSubmitting ? 'show' : ''}`} style={{ display: submissionStatus.isSubmitting ? 'block' : 'none' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body text-center p-4">
                        <div className="submission-progress mb-4">
                            <div className="progress" style={{ height: '10px' }}>
                                <div 
                                    className="progress-bar progress-bar-striped progress-bar-animated" 
                                    role="progressbar" 
                                    style={{ width: `${submissionStatus.progress}%` }}
                                ></div>
                            </div>
                            <p className="mt-2 mb-0">{submissionStatus.currentStep}</p>
                        </div>
                        {submissionStatus.error && (
                            <div className="alert alert-danger">
                                {submissionStatus.error}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // SDG mapping configuration
    const sdgIconMapping = {
        'Climate Action': _13,
        'Energy Efficiency': _7,
        'Water': _6,
        'Waste': _12,
        'Human & Labour Rights': _8,
        'Living Wage': _5,
        'Gender Equality': _5,
        'Anti-Corruption': _16
    };

    // SDG descriptions
    const sdgDescriptions = {
        'Water': {
            english: 'Ensure availability and sustainable management of water and sanitation for all',
            malay: 'Memastikan ketersediaan dan pengurusan air dan sanitasi yang mampan untuk semua'
        },
        'Human & Labour Rights': {
            english: 'Promote sustained, inclusive and sustainable economic growth, full and productive employment and decent work for all',
            malay: 'Menggalakkan pertumbuhan ekonomi yang berterusan, inklusif dan mampan, pekerjaan penuh dan produktif serta pekerjaan yang layak untuk semua'
        },
        'Living Wage': {
            english: 'End poverty in all its forms everywhere and ensure decent work and economic growth',
            malay: 'Mengakhiri kemiskinan dalam semua bentuk di mana-mana dan memastikan pekerjaan yang layak dan pertumbuhan ekonomi'
        },
        'Gender Equality': {
            english: 'Achieve gender equality and empower all women and girls',
            malay: 'Mencapai kesaksamaan gender dan memperkasakan semua wanita dan gadis'
        },
        'Anti-Corruption': {
            english: 'Promote peaceful and inclusive societies, provide access to justice for all and build effective, accountable and inclusive institutions',
            malay: 'Menggalakkan masyarakat yang aman dan inklusif, menyediakan akses kepada keadilan untuk semua dan membina institusi yang berkesan, bertanggungjawab dan inklusif'
        },
        'Climate Action': {
            english: 'Take urgent action to combat climate change and its impacts',
            malay: 'Mengambil tindakan segera untuk memerangi perubahan iklim dan kesannya'
        },
        'Energy Efficiency': {
            english: 'Ensure access to affordable, reliable, sustainable and modern energy for all',
            malay: 'Memastikan akses kepada tenaga yang mampu milik, boleh dipercayai, mampan dan moden untuk semua'
        },
        'Waste': {
            english: 'Ensure sustainable consumption and production patterns',
            malay: 'Memastikan corak penggunaan dan pengeluaran yang mampan'
        }
    };

    // Get performance level color
    const getPerformanceLevel = (score) => {
        if (score >= 80) return { level: 'Leader', color: '#38761D' };
        if (score >= 60) return { level: 'Advanced', color: '#93C47D' };
        if (score >= 40) return { level: 'Intermediate', color: '#B6D7A8' };
        if (score >= 20) return { level: 'Beginner', color: '#FF9900' };
        return { level: 'Laggard', color: '#FF0000' };
    };

    return (
        <StyledDashboard className="dashboard-container">
            <WelcomeModal />
            <SubmissionProgressModal />
            <div className="assessment-selector">
                <div 
                    className={`assessment-card ${activeTab === 'regular' ? 'active' : ''}`}
                    onClick={() => setActiveTab('regular')}
                >
                    <h3>{languageUse === 'English' ? 'Readiness Assessment' : 'Évaluation de préparation'}</h3>
                    <p>
                        {languageUse === 'English' 
                            ? 'Comprehensive evaluation of your organization\'s ESG performance across all dimensions.'
                            : 'Évaluation complète de la performance ESG de votre organisation à travers toutes les dimensions.'}
                    </p>
                    <div className="meta">
                        <span className="icon">📊</span>
                        <span>
                            {languageUse === 'English' 
                                ? 'Full assessment with detailed scoring'
                                : 'Évaluation complète avec notation détaillée'}
                        </span>
                    </div>
                </div>

                <div 
                    className={`assessment-card ${activeTab === 'progress' ? 'active' : ''}`}
                    onClick={() => setActiveTab('progress')}
                >
                    <h3>{languageUse === 'English' ? 'Progress Assessment' : 'Évaluation des progrès'}</h3>
                    <p>
                        {languageUse === 'English'
                            ? 'Track your ongoing improvements and monitor progress in specific areas.'
                            : 'Suivez vos améliorations continues et surveillez les progrès dans des domaines spécifiques.'}
                    </p>
                    <div className="meta">
                        <span className="icon">📈</span>
                        <span>
                            {languageUse === 'English'
                                ? 'Progress tracking and improvement monitoring'
                                : 'Suivi des progrès et surveillance des améliorations'}
                        </span>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center p-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    {/* Regular Assessment Content */}
                    {activeTab === 'regular' && (
                        <Fragment>
                            <div>
                                <Toaster />
                            </div>
                            <NavbarComponent />

                            <Container>
                                <div className="d-flex justify-content-between">
                                    <h2 className="mb-4">{languageUse === 'English' ? "Summary ESG Maturity & Action Report" : "Rapport sommaire de maturité et d'action ESG"}</h2>
                                    {/* <p>{languageUse}</p> */}
                                    <div className="d-none d-lg-block d-flex gap-2">
                                        <Button className="authButton login-btn-min-width mb-4" variant='outline-success' onClick={() => {
                                            toast.success(languageUse === 'English' ? 'Regenerating your ESG report...' : 'Régénération de votre rapport ESG...');
                                            pdfDownloadFunc();
                                        }}>
                                            🔄 {languageUse === 'English' ? 'Regenerate Report' : 'Régénérer le rapport'}
                                        </Button>
                                        <Button className="authButton login-btn-min-width mb-4" variant='outline-primary' onClick={pdfDownloadFunc}>
                                            {isLoading ? <Spinner variant='dark' size='sm' /> : <><BiImport style={{ fontSize: "20px", marginBottom: '3px' }}></BiImport> {languageUse === 'English' ? ' Download Report' : ' Télécharger le rapport'}</>}
                                        </Button>
                                        {certificateApproved && (
                                            <Button className="authButton login-btn-min-width mb-4" variant='outline-warning' onClick={certificateDownloadFunc}>
                                                {isLoading ? <Spinner variant='dark' size='sm' /> : <><BiImport style={{ fontSize: "20px", marginBottom: '3px' }}></BiImport> {languageUse === 'English' ? ' Download Certificate' : ' Muat Turun Sijil'}</>}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Certificate Approval Status Message */}
                                {!certificateApproved && certificateApprovalStatus !== null && (
                                    <div className={`alert mb-4 ${certificateApprovalStatus === 'pending' ? 'alert-warning' : certificateApprovalStatus === 'rejected' ? 'alert-danger' : 'alert-info'}`} role="alert">
                                        <h5 className="alert-heading">
                                            {languageUse === 'English' ? '📜 Certificate Status' : '📜 Status Sijil'}
                                        </h5>
                                        <p className="mb-0">
                                            {certificateApprovalStatus === 'pending' 
                                                ? (languageUse === 'English' 
                                                    ? 'Your certificate is pending admin approval. You will be able to download it once approved.' 
                                                    : 'Sijil anda sedang menunggu kelulusan admin. Anda akan dapat memuat turun setelah diluluskan.')
                                                : certificateApprovalStatus === 'rejected'
                                                ? (languageUse === 'English' 
                                                    ? 'Your certificate request was rejected. Please contact support for more information.' 
                                                    : 'Permintaan sijil anda telah ditolak. Sila hubungi sokongan untuk maklumat lanjut.')
                                                : (languageUse === 'English' 
                                                    ? 'Certificate is not available yet. Please complete your assessment and wait for admin approval.' 
                                                    : 'Sijil belum tersedia. Sila lengkapkan penilaian anda dan tunggu kelulusan admin.')}
                                        </p>
                                    </div>
                                )}
                                
                                {/* Certificate Verification URL Display */}
                                {certificateVerificationUrl && certificateApproved && (
                                    <div className="alert alert-info mb-4" role="alert">
                                        <h5 className="alert-heading">
                                            {languageUse === 'English' ? '📜 Certificate Verification URL' : '📜 URL Pengesahan Sijil'}
                                        </h5>
                                        <p className="mb-2">
                                            {languageUse === 'English' 
                                                ? 'Share this link to verify your certificate publicly:' 
                                                : 'Kongsi pautan ini untuk mengesahkan sijil anda secara awam:'}
                                        </p>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <code className="bg-white p-2 rounded border" style={{ flex: 1, minWidth: '200px' }}>
                                                {certificateVerificationUrl}
                                            </code>
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(certificateVerificationUrl);
                                                    toast.success(languageUse === 'English' ? 'URL copied to clipboard!' : 'URL disalin ke papan klip!');
                                                }}
                                            >
                                                {languageUse === 'English' ? 'Copy' : 'Salin'}
                                            </Button>
                                            <Button 
                                                variant="outline-success" 
                                                size="sm"
                                                onClick={() => window.open(certificateVerificationUrl, '_blank')}
                                            >
                                                {languageUse === 'English' ? 'Open' : 'Buka'}
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                
                                <div className="profile">
                                    <Row className="profile p-2 mb-0">
                                        <Col className="" md="6" sm="12">
                                            <p className="mb-0">{languageUse === 'English' ? 'Current Report Dated' : 'Laporan Semasa Bertarikh'} : <span className="bold">{moment(profileData?.updatedAt).format("DD-MM-YYYY")}</span></p>
                                            <p className="mb-0">{languageUse === 'English' ? 'Company' : 'Syarikat'} : <span className="bold">{profileData?.company}</span></p>
                                            <p className="mb-0">{languageUse === 'English' ? 'Company Registration Number' : 'No. Pendaftaran Syarikat'} : <span className="fw-bold">{profileData?.companyReg}</span></p>
                                            <p className="mb-0">{languageUse === 'English' ? 'Username' : 'Nama Pengguna'} : <span className="bold">{profileData?.username}</span></p>
                                            <p className="mb-0">
                                                {languageUse === 'English' ? 'State' : 'Negeri'} : <span className="fw-bold">{profileData?.state}</span>
                                            </p>
                                        </Col>
                                        <Col className="" md="6" sm="12">
                                            <p className="mb-0">{languageUse === 'English' ? 'Last Submission Date' : 'Tarikh Penyerahan Terakhir'} :{" "}<span className="bold">{lastSubmission ? moment(lastSubmission).format("DD-MM-YYYY") : 'No Previous Submission'}</span></p>
                                            <p className="mb-0">{languageUse === 'English' ? 'Industry' : 'Industri'} :{" "}<span className="bold">{languageUse === 'English' ? (profileData?.industry?.name || 'N/A') : (profileData?.industry?.name_Malay || 'N/A')}</span></p>
                                            <p className="mb-0">
                                                Email : <span className="bold">{profileData?.email}</span>
                                            </p>
                                            <p className="mb-0">
                                                {languageUse === 'English' ? 'Country' : 'Negara'} : <span className="fw-bold">{profileData?.country}</span>
                                            </p>
                                            <p className="mb-0">
                                                {languageUse === 'English' ? 'City' : 'Bandar'} : <span className="fw-bold">{profileData?.city}</span>
                                            </p>
                                        </Col>

                                    </Row>
                                    <Row className="profile px-2 mb-0">
                                        <span><hr></hr></span>
                                    </Row>

                                    {dimensionData?.length > 0 &&
                                        <Row className="profile p-2 mb-4">
                                            <Col className="" md="6" sm="12">
                                                <p>
                                                    {languageUse === 'English' ? 'ESG Maturity Level' : 'Tahap Kematangan ESG'} : <span className="fw-bold" style={{ color: colorLeval.find(item => item.leval === ESGperformanceData?.leval)?.color_code }}>{languageUse === 'English' ? ESGperformanceData?.leval : ESGperformanceData?.data?.leval_Malay}</span>
                                                </p>
                                                {languageUse === 'English' ? <p className="mb-0">
                                                    {dimensionData[1]?.dimension} Maturity Level : <span className="fw-bold" style={{ color: colorLeval.find(item => item.leval === dimensionData[1]?.leval)?.color_code }}>{dimensionData[1]?.leval}</span>
                                                </p> : <p className="mb-0">
                                                    Tahap Kematangan {dimensionData[1]?.dimension_Malay} : <span className="fw-bold" style={{ color: colorLeval.find(item => item.leval === dimensionData[1]?.leval)?.color_code }}>{dimensionData[1]?.performance?.leval_Malay}</span>
                                                </p>}
                                            </Col>
                                            <Col className="" md="6" sm="12">
                                                {languageUse === 'English' ? <p className="mb-0">
                                                    {dimensionData[0]?.dimension} Maturity Level : <span className="fw-bold" style={{ color: colorLeval.find(item => item.leval === dimensionData[0]?.leval)?.color_code }}>{dimensionData[0]?.leval}</span>
                                                </p> : <p className="mb-0">
                                                    Tahap Kematangan {dimensionData[0]?.dimension_Malay} : <span className="fw-bold" style={{ color: colorLeval.find(item => item.leval === dimensionData[0]?.leval)?.color_code }}>{dimensionData[0]?.performance?.leval_Malay}</span>
                                                </p>}
                                                {languageUse === 'English' ? <p className="mb-0">
                                                    {dimensionData[2]?.dimension} Maturity Level : <span className="fw-bold" style={{ color: colorLeval.find(item => item.leval === dimensionData[2]?.leval)?.color_code }}>{dimensionData[2]?.leval}</span>
                                                </p> : <p className="mb-0">
                                                    Tahap Kematangan {dimensionData[2]?.dimension_Malay} : <span className="fw-bold" style={{ color: colorLeval.find(item => item.leval === dimensionData[2]?.leval)?.color_code }}>{dimensionData[2]?.performance?.leval_Malay}</span>
                                                </p>}
                                            </Col>
                                        </Row>
                                    }

                                </div>

                                <Row className="p-0 my-2 d-none d-lg-block">
                                    <p className="fw-bold px-0">ESG Maturity Level:</p>
                                    <table className="table table-bordered border-0">
                                        <tbody>
                                            <tr className="border-0">
                                                <td className="ps-3 border-0 text-center fw-bold text-dark bg-Laggard">
                                                    <ESGMaturityTooltip id="laggard-tooltip" text={ESG_MATURITY_LEVELS.LAGGARD.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.LAGGARD.label : ESG_MATURITY_LEVELS.LAGGARD.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 border-0 text-center fw-bold text-dark bg-Beginner">
                                                    <ESGMaturityTooltip id="beginner-tooltip" text={ESG_MATURITY_LEVELS.BEGINNER.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.BEGINNER.label : ESG_MATURITY_LEVELS.BEGINNER.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 border-0 text-center fw-bold text-dark bg-Intermediate">
                                                    <ESGMaturityTooltip id="intermediate-tooltip" text={ESG_MATURITY_LEVELS.INTERMEDIATE.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.INTERMEDIATE.label : ESG_MATURITY_LEVELS.INTERMEDIATE.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 border-0 text-center fw-bold text-white bg-Advanced">
                                                    <ESGMaturityTooltip id="advanced-tooltip" text={ESG_MATURITY_LEVELS.ADVANCED.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.ADVANCED.label : ESG_MATURITY_LEVELS.ADVANCED.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 border-0 text-center fw-bold text-white bg-Leader">
                                                    <ESGMaturityTooltip id="leader-tooltip" text={ESG_MATURITY_LEVELS.LEADER.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.LEADER.label : ESG_MATURITY_LEVELS.LEADER.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                            </tr>
                                            <tr className="border-0">
                                                <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Laggard': ESGperformanceData?.leval === 'Laggard' || ESGperformanceData?.data?.leval_French === 'Retardataire', })}>{languageUse === 'English' ? ESGperformanceData?.leval === 'Laggard' ? <BsCheckCircleFill className="text-white" /> : '' : ESGperformanceData?.data?.leval_French === 'Retardataire' ? <BsCheckCircleFill className="text-white" /> : ''}</td>

                                                <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Beginner': ESGperformanceData?.leval === 'Beginner' || ESGperformanceData?.data?.leval_French === 'Débutant', })}>{languageUse === 'English' ? ESGperformanceData?.leval === 'Beginner' ? <BsCheckCircleFill className="text-white" /> : '' : ESGperformanceData?.data?.leval_French === 'Débutant' ? <BsCheckCircleFill className="text-white" /> : ''}</td>

                                                <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Intermediate': ESGperformanceData?.leval === 'Intermediate' || ESGperformanceData?.data?.leval_French === 'Intermédiaire', })}>{languageUse === 'English' ? ESGperformanceData?.leval === 'Intermediate' ? <BsCheckCircleFill className="text-white" /> : '' : ESGperformanceData?.data?.leval_French === 'Intermédiaire' ? <BsCheckCircleFill className="text-white" /> : ''}</td>

                                                <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Advanced': ESGperformanceData?.leval === 'Advanced' || ESGperformanceData?.data?.leval_French === 'Avancé', })}>{languageUse === 'English' ? ESGperformanceData?.leval === 'Advanced' ? <BsCheckCircleFill className="text-white" /> : '' : ESGperformanceData?.data?.leval_French === 'Avancé' ? <BsCheckCircleFill className="text-white" /> : ''}</td>

                                                <td className={classNames("ps-3 border-0 text-center fw-bold", { 'bg-Leader': ESGperformanceData?.leval === 'Leader' || ESGperformanceData?.data?.leval_French === 'Leader', })}>{languageUse === 'English' ? ESGperformanceData?.leval === 'Leader' ? <BsCheckCircleFill className="text-white" /> : '' : ESGperformanceData?.data?.leval_French === 'Leader' ? <BsCheckCircleFill className="text-white" /> : ''}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </Row>

                                {/* For Small device */}
                                <Row className="p-0 my-2 d-block d-lg-none">
                                    <h5 className="fw-bold">ESG Maturity Level:</h5>
                                    <table className="table table-bordered">
                                        <tbody>
                                            <tr>
                                                <td className="ps-3 fw-bold text-dark" style={{ backgroundColor: ESG_MATURITY_LEVELS.LAGGARD.color }}>
                                                    <ESGMaturityTooltip id="laggard-mobile-tooltip" text={ESG_MATURITY_LEVELS.LAGGARD.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.LAGGARD.label : ESG_MATURITY_LEVELS.LAGGARD.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 fw-bold text-center">
                                                    {languageUse === 'English' ? ESGperformanceData?.leval === 'Laggard' ? <BsCheckCircleFill /> : '' : ESGperformanceData?.data?.leval_French === 'Retardataire' ? <BsCheckCircleFill /> : ''}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td className="ps-3 fw-bold text-dark" style={{ backgroundColor: ESG_MATURITY_LEVELS.BEGINNER.color }}>
                                                    <ESGMaturityTooltip id="beginner-mobile-tooltip" text={ESG_MATURITY_LEVELS.BEGINNER.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.BEGINNER.label : ESG_MATURITY_LEVELS.BEGINNER.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 fw-bold text-center">
                                                    {languageUse === 'English' ? ESGperformanceData?.leval === 'Beginner' ? <BsCheckCircleFill /> : '' : ESGperformanceData?.data?.leval_French === 'Débutant' ? <BsCheckCircleFill /> : ''}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td className="ps-3 fw-bold text-dark" style={{ backgroundColor: ESG_MATURITY_LEVELS.INTERMEDIATE.color }}>
                                                    <ESGMaturityTooltip id="intermediate-mobile-tooltip" text={ESG_MATURITY_LEVELS.INTERMEDIATE.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.INTERMEDIATE.label : ESG_MATURITY_LEVELS.INTERMEDIATE.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 fw-bold text-center">
                                                    {languageUse === 'English' ? ESGperformanceData?.leval === 'Intermediate' ? <BsCheckCircleFill /> : '' : ESGperformanceData?.data?.leval_French === 'Intermédiaire' ? <BsCheckCircleFill /> : ''}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td className="ps-3 fw-bold text-dark" style={{ backgroundColor: ESG_MATURITY_LEVELS.ADVANCED.color }}>
                                                    <ESGMaturityTooltip id="advanced-mobile-tooltip" text={ESG_MATURITY_LEVELS.ADVANCED.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.ADVANCED.label : ESG_MATURITY_LEVELS.ADVANCED.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 fw-bold text-center">
                                                    {languageUse === 'English' ? ESGperformanceData?.leval === 'Advanced' ? <BsCheckCircleFill /> : '' : ESGperformanceData?.data?.leval_French === 'Avancé' ? <BsCheckCircleFill /> : ''}
                                                </td>
                                            </tr>

                                            <tr>
                                                <td className="ps-3 fw-bold text-dark" style={{ backgroundColor: ESG_MATURITY_LEVELS.LEADER.color }}>
                                                    <ESGMaturityTooltip id="leader-mobile-tooltip" text={ESG_MATURITY_LEVELS.LEADER.description}>
                                                        {languageUse === 'English' ? ESG_MATURITY_LEVELS.LEADER.label : ESG_MATURITY_LEVELS.LEADER.frenchLabel}
                                                    </ESGMaturityTooltip>
                                                </td>
                                                <td className="ps-3 fw-bold text-center">
                                                    {languageUse === 'English' ? ESGperformanceData?.leval === 'Leader' ? <BsCheckCircleFill /> : '' : ESGperformanceData?.data?.leval_French === 'Leader' ? <BsCheckCircleFill /> : ''}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </Row>

                                <Row className="justify-content-between">
                                    <Col
                                        className="history-text-color px-0 mb-4 mb-md-0"
                                        md="5"
                                        sm="12"
                                    >
                                        <h4 className="ps-3 pt-2 mb-4">{languageUse === 'English' ? 'Score of Category' : 'Skor Kategori'}</h4>

                                        <div className="mt-5 d-flex justify-content-center align-items-center">
                                            <Bar data={regularChartData} options={chartOptions} height={150}></Bar>
                                        </div>
                                    </Col>
                                    <Col className="history-text-color px-0" md="6" sm="12">
                                        <div>
                                            <h4 className="ps-3 pt-2 mb-4">{languageUse === 'English' ? "Score of Indicator" : "Skor Penunjuk"}</h4>
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-responsive">
                                                    <thead className="table-head">
                                                        <tr>
                                                            <td scope="col">{languageUse === 'English' ? "Dimension" : "Dimensi"}</td>
                                                            <td scope="col">{languageUse === 'English' ? "Indicator" : "Penunjuk"}</td>
                                                            <td scope="col" className="text-center">
                                                                {languageUse === 'English' ? "Your Score" : "Skor Anda"}
                                                            </td>
                                                            <td scope="col" className="text-center">
                                                                {languageUse === 'English' ? "Peer Score" : "Skor Rakan Industri"}
                                                            </td>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {indicatorData?.length > 0 && [...new Set(indicatorData.map(item => item?.dimension?._id))].map((it, ind) => {
                                                            return (
                                                                indicatorData?.filter(item => item?.dimension?._id === it).map((item, index) => (
                                                                    <tr key={index}>
                                                                        {index === 0 && <td rowSpan={indicatorData?.filter(item => item?.dimension?._id === it).length} style={{ backgroundColor: '#f4fbfe' }}>{languageUse === 'English' ? item?.dimension?.name ? `${item?.dimension?.name}` : '--' : item?.dimension?.name_Malay ? `${item?.dimension?.name_Malay}` : '--'}</td>}

                                                                        <td className="">{languageUse === 'English' ? item.name ? `${item?.name}` : '--' : item.name_Malay ? `${item?.name_Malay}` : '--'}</td>
                                                                        <td className="text-center">
                                                                            {normalizeScore(item?.correct, item?.total)}%
                                                                        </td>
                                                                        <td className="text-center">
                                                                            {isIndustryScoreAvailable(item) 
                                                                                ? `${item.industryScore || normalizeScore(item.originalIndustryScore || item.industryScore, item.total)}%`
                                                                                : (languageUse === 'English' ? 'N/A' : 'Tidak tersedia')
                                                                            }
                                                                        </td>
                                                                    </tr>

                                                                ))
                                                            )
                                                        })}

                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <Row className="border mt-5" >
                                    <h4 className="pt-3 px-5">{languageUse === 'English' ? "Peer Comparison Bar" : "Carta Perbandingan Rakan Industri"}</h4>
                                    <div className="mt-5">
                                        {/* <Bar className="ps-1 pe-4" data={props.horizontalData} options={props.horoizontalOptions}></Bar> */}
                                        <Bar style={{ marginRight: '5rem', paddingRight: '5rem', marginBottom: '5px' }} data={horizontalData} options={horoizontalOptions}></Bar>
                                    </div>
                                </Row>

                                <Row className="mt-4">
                                    <h4 className="fw-bold mb-2">{languageUse === 'English' ? "ESG performance" : "Prestasi ESG"}</h4>

                                    {languageUse === 'English' ? <span className="textColor mb-2">{profileData?.company} has an {ESGperformanceData?.leval} level</span> : <span className="textColor mb-2">{profileData?.company} memiliki tahap {ESGperformanceData?.data?.leval_Malay}</span>}

                                    {/* <p className="">{ESGperformanceData?.data?.esgDescription.replace(/SME X/g, profileData?.company)}</p> */}

                                    <p className="">{languageUse === 'English' ? ESGperformanceData?.data?.esgDescription && ESGperformanceData?.data?.esgDescription.replace(/SME X/g, profileData?.company) : ESGperformanceData?.data?.esgDescription_Malay && ESGperformanceData?.data?.esgDescription_Malay}</p>
                                    <span>
                                        <hr></hr>
                                    </span>
                                </Row>

                                {dimensionData?.length > 0 && dimensionData?.map((dimensionItem, dimensionIndex) => (
                                    <Row key={dimensionIndex} className="mt-1">
                                        <h4 className="mb-1 fw-bold">{`${languageUse === 'English' ? `${dimensionItem?.dimension} performance` : `Prestasi ${dimensionItem?.dimension_Malay}`}`} </h4>
                                        {/* <span className="textColor mb-2">{profileData?.company} has an {dimensionItem?.leval} level</span> */}

                                        {languageUse === 'English' ? <>
                                            <span className="textColor mb-2">{`SME X shows ${dimensionItem?.leval} level in the ${languageUse === 'English' ? dimensionItem?.dimension : dimensionItem?.dimension_Malay} dimension which is evident for the ${languageUse === 'English' ? selectedIndicatorData?.find(element => element?.dimension?._id === dimensionItem?.id)?.name : selectedIndicatorData?.find(element => element?.dimension?._id === dimensionItem?.id)?.name_Malay} indicator which scores ${higherLowerDetector(selectedIndicatorData?.find(element => element?.dimension?._id === dimensionItem?.id))} than peers.`.replace(/SME X/g, profileData?.company)}</span>
                                        </> : <>
                                            <span className="textColor mb-2">{`SME X memiliki tahap ${dimensionItem?.performance?.leval_Malay} dalam ${languageUse === 'English' ? dimensionItem?.dimension : dimensionItem?.dimension_Malay} dimensi Persekitaran selaras dengan skor ${higherLowerDetector(selectedIndicatorData?.find(element => element?.dimension?._id === dimensionItem?.id))} rendah berbanding rakan industri dalam kategori ${languageUse === 'English' ? selectedIndicatorData?.find(element => element?.dimension?._id === dimensionItem?.id)?.name : selectedIndicatorData?.find(element => element?.dimension?._id === dimensionItem?.id)?.name_Malay}.`.replace(/SME X/g, profileData?.company)}</span>

                                        </>}

                                        {/* <p className="mb-2">{dimensionItem?.performance?.description?.replace(/SME X/g, profileData?.company)}</p> */}
                                        <p className="mb-2">{languageUse === 'English' ? dimensionItem?.performance?.description.replace(/SME X/g, profileData?.company) : dimensionItem?.performance?.description_Malay.replace(/SME X/g, profileData?.company)}</p>
                                        <h4 className="fw-bold">{languageUse === 'English' ? 'Recommended action' : 'Tindakan Yang Disarankan'}:</h4>
                                        <p className="mb-2">{languageUse === 'English' ? dimensionItem?.performance?.action : dimensionItem?.performance?.action_Malay}</p>

                                        <span>
                                            <hr></hr>
                                        </span>
                                    </Row>
                                ))}

                                <Row>
                                    <h4 className="pt-2 mb-4 fw-bold">SDG MAPPING</h4>
                                    <div className="px-2">
                                        <div className="sdg-cards-container mb-4" style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '15px',
                                            justifyContent: 'flex-start'
                                        }}>
                                            {indicatorData?.length > 0 && indicatorData?.map((item, index) => {
                                                const { yourScore, industryAvg, description } = processSDGData(item);
                                                const { level: performanceLevel, color: levelColor } = getPerformanceLevel(parseFloat(yourScore));
                                                
                                                return (
                                                    <div key={index} style={{
                                                        width: '250px',
                                                        backgroundColor: '#ffffff',
                                                        padding: '20px',
                                                        borderRadius: '8px',
                                                        border: `2px solid ${levelColor}`,
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginBottom: '15px'
                                                        }}>
                                                            <img 
                                                                src={sdgIconMapping[item.name]} 
                                                                alt={item.name}
                                                                style={{
                                                                    width: '60px',
                                                                    height: '60px',
                                                                    marginRight: '15px'
                                                                }}
                                                            />
                                                            <h5 style={{
                                                                margin: 0,
                                                                color: '#2c3e50',
                                                                fontSize: '1rem'
                                                            }}>{languageUse === 'English' ? item?.name : item?.name_Malay}</h5>
                                                        </div>
                                                        
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            marginBottom: '8px'
                                                        }}>
                                                            <span>{languageUse === 'English' ? 'Your Score:' : 'Skor Anda:'}</span>
                                                            <span style={{ color: levelColor, fontWeight: '600' }}>{yourScore}%</span>
                                                        </div>
                                                        
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            marginBottom: '8px'
                                                        }}>
                                                            <span>{languageUse === 'English' ? 'Industry Avg:' : 'Purata Industri:'}</span>
                                                            <span style={{ color: '#6c757d' }}>
                                                                {industryAvg !== null && industryAvg !== undefined 
                                                                    ? `${industryAvg}%` 
                                                                    : (languageUse === 'English' ? 'N/A' : 'Tidak tersedia')
                                                                }
                                                            </span>
                                                        </div>
                                                        
                                                        <div style={{
                                                            backgroundColor: levelColor,
                                                            color: '#ffffff',
                                                            padding: '4px 8px',
                                                            borderRadius: '12px',
                                                            textAlign: 'center',
                                                            fontSize: '0.875rem',
                                                            marginBottom: '10px'
                                                        }}>
                                                            {performanceLevel}
                                                        </div>

                                                        <div style={{
                                                            fontSize: '0.875rem',
                                                            color: '#6c757d',
                                                            marginTop: '10px',
                                                            lineHeight: '1.4',
                                                            borderTop: '1px solid #e9ecef',
                                                            paddingTop: '10px'
                                                        }}>
                                                            {languageUse === 'English' ? description.english : description.malay}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </Row>

                                <h4 className="pt-2 mb-4 fw-bold">{languageUse === 'English' ? "Uploaded Documents" : "Dokumen Yang Dimuatnaik"}</h4>
                                {uploadedDocInfo?.length > 0 ? <div className="table-responsive">
                                    <table className="table table-bordered table-responsive">
                                        <thead className="text-color-primary fw-bold">
                                            <tr>
                                                <td scope="col">{languageUse === 'English' ? 'Dimension' : 'DImensi'}</td>
                                                <td scope="col">{languageUse === 'English' ? 'Name' : 'Nama'}</td>
                                                <td scope="col">{languageUse === 'English' ? 'Description' : 'Deskripsi'}</td>
                                                <td scope="col">{languageUse === 'English' ? 'Uploaded PDF' : 'pdf. dimuatnaik'}</td>
                                                <td scope="col">Actions</td>

                                            </tr>
                                        </thead>

                                        <tbody>
                                            {uploadedDocInfo?.length > 0 && uploadedDocInfo?.map((it, ind) => {
                                                return (
                                                    it?.files?.map((item, index) => (
                                                        <tr key={index}>
                                                            {index === 0 && <td rowSpan={it?.files?.length}>{languageUse === 'English' ? it?.dimension?.name : it?.dimension?.name_Malay}</td>}
                                                            {index === 0 && <td rowSpan={it?.files?.length}>{it?.name}</td>}
                                                            {index === 0 && <td rowSpan={it?.files?.length}>{it?.description}</td>}
                                                            <td className=''>{item ? `Document-${index + 1}` : '--'}</td>
                                                            <td className='text-color-primary'>
                                                                {/* <a href={`${baseUrl}${item}`} target="_blank"><AiOutlineEye className='me-3' role='button' /></a> */}
                                                                <AiOutlineEye className='me-2' role='button' onClick={() => viewDoc(item)} />
                                                                <a href={`${AWS_Url}${item}`} target="_blank"><BiImport className='' role='button' /></a>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )
                                            })
                                            }

                                        </tbody>
                                    </table>
                                </div> : <div className='w-100 d-flex align-items-center justify-content-center'>
                                    <p className=''>No Document Available</p>
                                </div>}

                                <h4 className="pt-2 mb-4 fw-bold">DISCLAIMER</h4>
                                <p style={{ fontSize: '.9rem' }}>
                                    The ESG maturity levels and score calculations provided on this website are based on self-declared data submissions by <span className="textColor">{profileData?.company}</span>. The information provided in this report shall not be relied upon as a substitute for professional advice. <span className="textColor">UNGCNSA</span> shall not be held liable for any errors, omissions, or inaccuracies in the data provided by <span className="textColor">{profileData?.company}</span> or for any actions taken in reliance on such information. <span className="textColor">{profileData?.company}</span> is solely responsible for the accuracy and completeness of the information they provide.
                                </p>
                                {/* <p className="fs-14">For more information & support, reach out to <a className="text-decoration-none" href="mailto:strategicpartnerships@alliancefg.com">strategicpartnerships@alliancefg.com</a></p> */}
                            </Container>

                            {/* PDF Report component moved to hidden section at bottom for printing */}
                        </Fragment>
                    )}

                    {/* Progress Assessment Content */}
                    {activeTab === 'progress' && (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h3>{languageUse === 'English' ? 'Progress Assessment Overview' : 'Aperçu de l\'évaluation des progrès'}</h3>
                                    <p className="text-muted">
                                        {languageUse === 'English' 
                                            ? `Last updated: ${lastSubmission ? moment(lastSubmission).format("DD-MM-YYYY") : 'No submissions yet'}`
                                            : `Dernière mise à jour : ${lastSubmission ? moment(lastSubmission).format("DD-MM-YYYY") : 'Aucune soumission pour le moment'}`
                                        }
                                    </p>
                                </div>
                                <div>
                                    <Button 
                                        variant="primary"
                                        className="me-2"
                                        onClick={() => navigate('/progress-assessment/new')}
                                    >
                                        {languageUse === 'English' ? 'Start New Assessment' : 'Commencer une nouvelle évaluation'}
                                    </Button>
                                    <Button 
                                        variant="outline-primary"
                                        onClick={() => navigate('/progress-assessment/history')}
                                    >
                                        {languageUse === 'English' ? 'View History' : 'Voir l\'historique'}
                                    </Button>
                                </div>
                            </div>

                            <div className="score-summary">
                                <div className="score-card">
                                    <h3>{languageUse === 'English' ? 'Overall Progress' : 'Kemajuan Keseluruhan'}</h3>
                                    <div className="score-value">{progressScorePercent}%</div>
                                    <div className="score-label">
                                        {languageUse === 'English' ? 'Based on all dimensions' : 'Berdasarkan semua dimensi'}
                                    </div>
                                </div>
                                {progressDimensionData.map((dimension, index) => (
                                    <div key={index} className="score-card">
                                        <h3>{languageUse === 'English' ? dimension.name : dimension.name_French}</h3>
                                        <div className="score-value">
                                            {((dimension.correct / dimension.total) * 100).toFixed(1)}%
                                        </div>
                                        <div className="score-label">
                                            {languageUse === 'English' 
                                                ? `${dimension.correct} of ${dimension.total} questions`
                                                : `${dimension.correct} daripada ${dimension.total} soalan`
                                            }
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Row>
                                <Col md={8}>
                                    <div className="chart-container">
                                        <Bar data={progressChartData} options={chartOptions} />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="quick-actions p-4 bg-light rounded">
                                        <h4 className="mb-3">
                                            {languageUse === 'English' ? 'Quick Actions' : 'Actions rapides'}
                                        </h4>
                                        <div className="d-grid gap-2">
                                            <Button 
                                                variant="outline-primary"
                                                onClick={() => navigate('/progress-assessment/questions')}
                                            >
                                                {languageUse === 'English' ? 'Continue Assessment' : 'Continuer l\'évaluation'}
                                            </Button>
                                            <Button 
                                                variant="outline-primary"
                                                onClick={() => navigate('/progress-assessment/reports')}
                                            >
                                                {languageUse === 'English' ? 'View Reports' : 'Voir les rapports'}
                                            </Button>
                                            <Button 
                                                variant="outline-primary"
                                                onClick={() => navigate('/progress-assessment/documents')}
                                            >
                                                {languageUse === 'English' ? 'Manage Documents' : 'Gérer les documents'}
                                            </Button>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Recent Activity Section */}
                            <div className="recent-activity mt-4 p-4 bg-white rounded">
                                <h4 className="mb-3">
                                    {languageUse === 'English' ? 'Recent Activity' : 'Aktiviti Terkini'}
                                </h4>
                                {progressQuestionAns.length > 0 ? (
                                    <div className="activity-list">
                                        {progressQuestionAns.slice(0, 5).map((answer, index) => (
                                            <div key={index} className="activity-item p-3 border-bottom">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div>
                                                        <h5 className="mb-2">
                                                            {languageUse === 'English' ? answer.question : answer.question_French}
                                                        </h5>
                                                        <p className="text-muted mb-0">
                                                            {Array.isArray(answer.answer) ? answer.answer.join(', ') : answer.answer}
                                                        </p>
                                                    </div>
                                                    <Badge bg="primary">
                                                        {languageUse === 'English' ? 'Answered' : 'Dijawab'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center text-muted p-4">
                                        {languageUse === 'English' 
                                            ? 'No recent activity. Start a new assessment to begin.'
                                            : 'Tiada aktiviti terkini. Mulakan penilaian baru untuk memulakan.'
                                        }
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}

            <Footer />
            <ShowPdfModal show={showPdfModalShow} handleClose={handleShowPdfModalShow} pdfItem={pdfItem} />
            
            {/* Hidden PDF report components - Always rendered but completely hidden, used only for printing */}
            <div style={{ 
                position: 'absolute', 
                left: '-9999px', 
                top: '-9999px', 
                width: '210mm', // A4 width for proper PDF rendering
                height: '297mm', // A4 height
                overflow: 'hidden',
                visibility: pdfContent ? 'visible' : 'hidden',
                opacity: 0,
                pointerEvents: 'none',
                zIndex: -1
            }}>
                <PdfReportPage2 
                    ref={componentRef} 
                    id={decryptedSubId} 
                    pdfContent={pdfContent} 
                    data={activeTab === 'regular' ? regularChartData : progressChartData} 
                    options={chartOptions} 
                    dimensionData={dimensionData}
                    indicatorData={indicatorData}
                    selectedIndicatorData={selectedIndicatorData}
                    profileData={profileData}
                    ESGperformanceData={ESGperformanceData}
                    optionalQuesData={optionalQuesData}
                    requiredQuestions={requiredQuestions}
                    overallVal={overallVal}
                    overallValLebel={overallValLebel}
                    horoizontalOptions={horoizontalOptions}
                    horizontalData={horizontalData}
                    uploadedDocInfo={uploadedDocInfo}
                    lastSubmission={lastSubmission}
                />
            </div>

            {/* Hidden Certificate component - Always rendered but completely hidden, used only for printing */}
            <div style={{ 
                position: 'absolute', 
                left: '-9999px', 
                top: '-9999px', 
                width: '210mm', // A4 width for proper PDF rendering
                height: '297mm', // A4 height
                overflow: 'hidden',
                visibility: certificatePdfContent ? 'visible' : 'hidden',
                opacity: 0,
                pointerEvents: 'none',
                zIndex: -1
            }}>
                <ESGCertificate 
                    ref={certificateRef} 
                    pdfContent={certificatePdfContent}
                    profileData={profileData}
                    ESGperformanceData={ESGperformanceData}
                    indicatorData={indicatorData}
                    overallScorePercent={overallVal && overallVal.length > 0 ? overallVal[overallVal.length - 1] : null}
                    verificationUrl={certificateVerificationUrl || ''}
                />
            </div>
        </StyledDashboard>
    );
};

export default Dashboard;